import { useState, useCallback } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  ConnectionMode,
  NodeTypes,
  OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { StepNode } from '@/lib/steps/step-node';
import { IOType } from '@erp/shared-types/node-graph';
import {
  XIcon,
  PlayIcon,
  FloppyDiskIcon,
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Separator } from 'src/components/ui/separator';

// Define custom node types
const nodeTypes: NodeTypes = {
  stepNode: StepNode,
};

// Initial hardcoded nodes for testing
const initialNodes: Node[] = [
  {
    id: '1',
    type: 'stepNode',
    data: {
      label: 'AP Email Event Trigger',
      stepType: 'event_trigger',
      emits: ['EMAIL_ROUTED_AP_INVOICE', 'EMAIL_ROUTED_PURCHASE_ORDER'],
      inputs: {
        source: { id: '1-input-source', ioType: IOType.INPUT },
      },
      outputs: {
        dataset: { id: '1-output-dataset', ioType: IOType.OUTPUT },
      },
    },
    position: { x: 50, y: 100 },
  },
  {
    id: '2',
    type: 'stepNode',
    data: {
      label: 'Transform',
      inputs: {
        data: { id: '2-input-data', ioType: IOType.INPUT },
        rules: { id: '2-input-rules', ioType: IOType.INPUT },
      },
      outputs: {
        transformed: { id: '2-output-transformed', ioType: IOType.OUTPUT },
      },
    },
    position: { x: 300, y: 100 },
  },
  {
    id: '3',
    type: 'stepNode',
    data: {
      label: 'Validate',
      emits: ['AP_INVOICE_DRAFT_CREATED'],
      inputs: {
        data: { id: '3-input-data', ioType: IOType.INPUT },
      },
      outputs: {
        valid: { id: '3-output-valid', ioType: IOType.OUTPUT },
        invalid: { id: '3-output-invalid', ioType: IOType.OUTPUT },
      },
    },
    position: { x: 550, y: 100 },
  },
  {
    id: '4',
    type: 'stepNode',
    data: {
      label: 'Aggregate',
      inputs: {
        dataset: { id: '4-input-dataset', ioType: IOType.INPUT },
      },
      outputs: {
        summary: { id: '4-output-summary', ioType: IOType.OUTPUT },
        count: { id: '4-output-count', ioType: IOType.OUTPUT },
        average: { id: '4-output-average', ioType: IOType.OUTPUT },
      },
    },
    position: { x: 800, y: 50 },
  },
  {
    id: '5',
    type: 'stepNode',
    data: {
      label: 'Export',
      inputs: {
        data: { id: '5-input-data', ioType: IOType.INPUT },
        format: { id: '5-input-format', ioType: IOType.INPUT },
      },
      outputs: {
        result: { id: '5-output-result', ioType: IOType.OUTPUT },
      },
    },
    position: { x: 800, y: 200 },
  },
];

// Initial hardcoded edges for testing
const initialEdges: Edge[] = [
  {
    id: 'e1-2',
    source: '1',
    sourceHandle: '1-output-dataset',
    target: '2',
    targetHandle: '2-input-data',
  },
  {
    id: 'e2-3',
    source: '2',
    sourceHandle: '2-output-transformed',
    target: '3',
    targetHandle: '3-input-data',
  },
  {
    id: 'e3-4',
    source: '3',
    sourceHandle: '3-output-valid',
    target: '4',
    targetHandle: '4-input-dataset',
  },
  {
    id: 'e3-5',
    source: '3',
    sourceHandle: '3-output-invalid',
    target: '5',
    targetHandle: '5-input-data',
  },
];

export function WorkflowPlaygroundPage() {
  const [nodes, _setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  const onConnect = useCallback(
    (params: Connection) => setEdges((els) => addEdge(params, els)),
    [setEdges],
  );

  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes }: OnSelectionChangeParams) => {
      if (selectedNodes.length === 1) {
        setSelectedNode(selectedNodes[0]);
      } else {
        setSelectedNode(null);
      }
    },
    [],
  );

  return (
    <div className="h-[calc(100vh-4rem-0.5rem)] w-full flex flex-col">
      {/* Toolbar */}
      <div className="border-y bg-background px-3 py-1.5 flex items-center gap-1.5 shrink-0">
        <Button variant="default" size="sm" disabled>
          <PlayIcon className="h-4 w-4 mr-2" />
          Run
        </Button>
        <Button variant="outline" size="sm" disabled>
          <FloppyDiskIcon className="h-4 w-4 mr-2" />
          Save
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button variant="ghost" size="icon" disabled>
          <ArrowUUpLeftIcon className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" disabled>
          <ArrowUUpRightIcon className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button variant="outline" size="sm" disabled>
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Node
        </Button>
      </div>

      {/* Content Area: Canvas + Side Panel */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* ReactFlow Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <ReactFlow
            proOptions={{ hideAttribution: true }}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            nodeTypes={nodeTypes}
            connectionMode={ConnectionMode.Strict}
            fitView
          >
            <Controls />
            <MiniMap />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </div>

        {/* Side Panel for Node Details */}
        {selectedNode && (
          <div className="w-96 border-l border-border bg-background flex flex-col">
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <h2 className="text-lg font-semibold">Node Details</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedNode(null)}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              <pre className="text-xs bg-muted p-3 rounded-md whitespace-pre-wrap break-words">
                {JSON.stringify(selectedNode, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
